import {Injectable} from '@angular/core';
import {ApiService} from "@core/api/api.service";
import {Util} from "@data/util/util";
import { Activity} from "@data/interefaces/client-portal.interfaces";
import {KpiSidebarItem} from "@shared/ag-kpi-sidebar/ag-kpi-sidebar.component.ds";
import {TimeUnit} from "@data/enums/data.enums";
import {ClientPortalActivityService} from "@data/client-portal/client-portal-activity.service";
import {EventLog, EventLogLineChartData} from "@data/interefaces/eventlog.interfaces";

@Injectable({
  providedIn: 'root'
})
export class PageActivityService {
  private readonly URL = '/v2/api/client-portal/page-activity';
  private readonly TITLE_ACTIVE = "Number of Active Sessions";
  private readonly TITLE_NEW_SESSIONS = "Number of New Sessions";
  private readonly TITLE_AVG_SESSION_DURATION = "Average Session Duration";
  private readonly TITLE_SUFFIX_IN_LAST = "in Last";
  private readonly TITLE_AVG_LOAD_TIME = "Average Time to Load";
  private readonly TIME_UNIT_AMOUNT = 1;
  private readonly KPI_CSS_ID = "page-activity-kpi";

  constructor(private _apiService: ApiService, private _clientPortalActivityService: ClientPortalActivityService) {
  }

  public getAllPageActivity(): Promise<Activity[]> {
    return this._apiService.makeGetServiceCall<Activity[]>(this.URL);
  }

  // KPIs
  public getPageActivityKpi(pageActivities: Activity[], timeUnit: TimeUnit | null): KpiSidebarItem[] {
    if (!timeUnit) {
      timeUnit = TimeUnit.HOUR;
    }
    const kpiItems: KpiSidebarItem[] = [];
    kpiItems.push(this.getCurrentActiveKpiItem(pageActivities));
    kpiItems.push(this.getNewSessionsKpiItem(pageActivities, timeUnit));
    kpiItems.push(this.getAverageSessionDurationKpiItem(pageActivities, timeUnit));
    kpiItems.push(this.getAverageLoadTimeFor(pageActivities, 'home', timeUnit));
    kpiItems.push(this.getAverageLoadTimeFor(pageActivities, 'reports', timeUnit));
    kpiItems.push(this.getAverageLoadTimeFor(pageActivities, 'dashboard', timeUnit));
    return kpiItems;
  }

  private getCurrentActiveKpiItem(pageActivities: Activity[]): KpiSidebarItem {
    return {
      id: this.KPI_CSS_ID,
      title: this.TITLE_ACTIVE,
      value: this._clientPortalActivityService.getCountUniqueSessionIdsInActivities(
        pageActivities.filter(activity => activity.sessionEndTimestamp === null)
      )
    };
  }

  private getNewSessionsKpiItem(pageActivities: Activity[], timeUnit: TimeUnit,): KpiSidebarItem {
    const countFunc = (timeUnitStart: Date): number =>
      this._clientPortalActivityService.getCountUniqueSessionIdsInActivities(
        this._clientPortalActivityService.filterActivitiesBySessionStartInTimeRange(pageActivities, timeUnitStart, undefined)
      );

    const title = `${this.TITLE_NEW_SESSIONS} ${this.TITLE_SUFFIX_IN_LAST}`;
    return this._clientPortalActivityService.createKpiItem(timeUnit, title , this.KPI_CSS_ID ,this.TIME_UNIT_AMOUNT, countFunc);
  }

  private getAverageSessionDurationKpiItem(pageActivities: Activity[], timeUnit: TimeUnit,): KpiSidebarItem {
    const funct = (timeUnitStart: Date): string =>
      Util.formatMillisecondDuration(this._clientPortalActivityService.getAverageSessionDuration(
        this._clientPortalActivityService.filterActivitiesBySessionStartInTimeRange(pageActivities, timeUnitStart, undefined)
      ));

    const title  = `${this.TITLE_AVG_SESSION_DURATION}  ${this.TITLE_SUFFIX_IN_LAST}`;
    return this._clientPortalActivityService.createKpiItem(timeUnit, title,  this.KPI_CSS_ID, this.TIME_UNIT_AMOUNT, funct);
  }

  private getAverageLoadTimeFor(pageActivities: Activity[], pageName:string, timeUnit: TimeUnit,): KpiSidebarItem {
    pageActivities = pageActivities.filter(activity => activity.loadUrl.includes(pageName));

    const title = `${this.TITLE_AVG_LOAD_TIME} the ${this.getUserFacingPageName(pageName)} ${this.TITLE_SUFFIX_IN_LAST}`;
    return this._clientPortalActivityService.getAverageLoadTimeKpi(pageActivities, timeUnit,title,this.KPI_CSS_ID);
  }

  public getUserFacingPageName(internalPageName:string){
    switch (internalPageName){
      case 'dashboard':
        return 'Report Page';
      case 'reports':
        return 'Report List Page';
      case 'home':
        return 'Home Page';
      default:
        return internalPageName;
    }
  }
}
